import { matchPath } from "react-router-dom";

export type BreadcrumbItem = {
  labelKey: string;
  /** Optional dynamic label (already translated / display text). Wins over labelKey when set. */
  label?: string;
  href?: string;
};

type BreadcrumbRule = {
  path: string;
  crumbs: (params: Record<string, string | undefined>) => BreadcrumbItem[];
};

const general = (href?: string): BreadcrumbItem => ({
  labelKey: "general",
  href: href ?? undefined,
});

/** General sidebar module routes — more specific paths first. */
const GENERAL_BREADCRUMB_RULES: BreadcrumbRule[] = [
  // Units
  {
    path: "/units",
    crumbs: () => [general(), { labelKey: "units" }],
  },
  {
    path: "/unit/create",
    crumbs: () => [
      general(),
      { labelKey: "units", href: "/units" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/unit/:id",
    crumbs: () => [
      general(),
      { labelKey: "units", href: "/units" },
      { labelKey: "Details" },
    ],
  },

  // Statuses
  {
    path: "/status/:typename/create",
    crumbs: (p) => [
      general(),
      {
        labelKey: "Statuses",
        href: p.typename ? `/status/${p.typename}` : "/status/packages",
      },
      { labelKey: "create" },
    ],
  },
  {
    path: "/status/:typename/edit/:id",
    crumbs: (p) => [
      general(),
      {
        labelKey: "Statuses",
        href: p.typename ? `/status/${p.typename}` : "/status/packages",
      },
      { labelKey: "Details" },
    ],
  },
  {
    path: "/status/:typename",
    crumbs: () => [general(), { labelKey: "Statuses" }],
  },

  // Inspection forms + nested
  {
    path: "/inspection-forms",
    crumbs: () => [general(), { labelKey: "inspectionForms" }],
  },
  {
    path: "/inspection-form/create",
    crumbs: () => [
      general(),
      { labelKey: "inspectionForms", href: "/inspection-forms" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/inspection-form/:id",
    crumbs: () => [
      general(),
      { labelKey: "inspectionForms", href: "/inspection-forms" },
      { labelKey: "Details" },
    ],
  },
  {
    path: "/inspection-sections/:id",
    crumbs: (p) => [
      general(),
      { labelKey: "inspectionForms", href: "/inspection-forms" },
      {
        labelKey: "Details",
        href: p.id ? `/inspection-form/${p.id}` : undefined,
      },
      { labelKey: "sections" },
    ],
  },
  {
    path: "/inspection-section/:id/create",
    crumbs: (p) => [
      general(),
      { labelKey: "inspectionForms", href: "/inspection-forms" },
      {
        labelKey: "Details",
        href: p.id ? `/inspection-form/${p.id}` : undefined,
      },
      {
        labelKey: "sections",
        href: p.id ? `/inspection-sections/${p.id}` : undefined,
      },
      { labelKey: "create" },
    ],
  },
  {
    path: "/inspection-section/:id/edit/:isid",
    crumbs: (p) => [
      general(),
      { labelKey: "inspectionForms", href: "/inspection-forms" },
      {
        labelKey: "Details",
        href: p.id ? `/inspection-form/${p.id}` : undefined,
      },
      {
        labelKey: "sections",
        href: p.id ? `/inspection-sections/${p.id}` : undefined,
      },
      { labelKey: "Details" },
    ],
  },
  {
    path: "/inspection-form-items/:id",
    crumbs: (p) => [
      general(),
      { labelKey: "inspectionForms", href: "/inspection-forms" },
      {
        labelKey: "Details",
        href: p.id ? `/inspection-form/${p.id}` : undefined,
      },
      { labelKey: "inspectionFormItems" },
    ],
  },
  {
    path: "/inspection-form-item/:id/create",
    crumbs: (p) => [
      general(),
      { labelKey: "inspectionForms", href: "/inspection-forms" },
      {
        labelKey: "Details",
        href: p.id ? `/inspection-form/${p.id}` : undefined,
      },
      {
        labelKey: "inspectionFormItems",
        href: p.id ? `/inspection-form-items/${p.id}` : undefined,
      },
      { labelKey: "create" },
    ],
  },
  {
    path: "/inspection-form-item/:id/edit/:ifid",
    crumbs: (p) => [
      general(),
      { labelKey: "inspectionForms", href: "/inspection-forms" },
      {
        labelKey: "Details",
        href: p.id ? `/inspection-form/${p.id}` : undefined,
      },
      {
        labelKey: "inspectionFormItems",
        href: p.id ? `/inspection-form-items/${p.id}` : undefined,
      },
      { labelKey: "Details" },
    ],
  },

  // States
  {
    path: "/states",
    crumbs: () => [general(), { labelKey: "states" }],
  },
  {
    path: "/state/create",
    crumbs: () => [
      general(),
      { labelKey: "states", href: "/states" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/state/:id",
    crumbs: () => [
      general(),
      { labelKey: "states", href: "/states" },
      { labelKey: "Details" },
    ],
  },

  // Checklists + items
  {
    path: "/checklists",
    crumbs: () => [general(), { labelKey: "Checklists" }],
  },
  {
    path: "/checklist/create",
    crumbs: () => [
      general(),
      { labelKey: "Checklists", href: "/checklists" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/checklist/:id",
    crumbs: () => [
      general(),
      { labelKey: "Checklists", href: "/checklists" },
      { labelKey: "Details" },
    ],
  },
  {
    path: "/checklistitems/:clid",
    crumbs: (p) => [
      general(),
      { labelKey: "Checklists", href: "/checklists" },
      {
        labelKey: "Details",
        href: p.clid ? `/checklist/${p.clid}` : undefined,
      },
      { labelKey: "checklistItems" },
    ],
  },
  {
    path: "/checklistitem/:clid/create",
    crumbs: (p) => [
      general(),
      { labelKey: "Checklists", href: "/checklists" },
      {
        labelKey: "Details",
        href: p.clid ? `/checklist/${p.clid}` : undefined,
      },
      {
        labelKey: "checklistItems",
        href: p.clid ? `/checklistitems/${p.clid}` : undefined,
      },
      { labelKey: "create" },
    ],
  },
  {
    path: "/checklistitem/:clid/edit/:id",
    crumbs: (p) => [
      general(),
      { labelKey: "Checklists", href: "/checklists" },
      {
        labelKey: "Details",
        href: p.clid ? `/checklist/${p.clid}` : undefined,
      },
      {
        labelKey: "checklistItems",
        href: p.clid ? `/checklistitems/${p.clid}` : undefined,
      },
      { labelKey: "Details" },
    ],
  },

  // Users
  {
    path: "/users",
    crumbs: () => [general(), { labelKey: "users" }],
  },
  {
    path: "/user/create",
    crumbs: () => [
      general(),
      { labelKey: "users", href: "/users" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/user/update-password/:id",
    crumbs: () => [
      general(),
      { labelKey: "users", href: "/users" },
      { labelKey: "updatePassword" },
    ],
  },
  {
    path: "/user/:id",
    crumbs: () => [
      general(),
      { labelKey: "users", href: "/users" },
      { labelKey: "Details" },
    ],
  },

  // Roles
  {
    path: "/roles",
    crumbs: () => [general(), { labelKey: "Roles" }],
  },
  {
    path: "/role/create",
    crumbs: () => [
      general(),
      { labelKey: "Roles", href: "/roles" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/role/assign/privilege/:id",
    crumbs: () => [
      general(),
      { labelKey: "Roles", href: "/roles" },
      { labelKey: "privileges" },
    ],
  },
  {
    path: "/role/:id",
    crumbs: () => [
      general(),
      { labelKey: "Roles", href: "/roles" },
      { labelKey: "Details" },
    ],
  },

  // User groups
  {
    path: "/user-groups",
    crumbs: () => [general(), { labelKey: "usersGroup" }],
  },
  {
    path: "/user-group/create",
    crumbs: () => [
      general(),
      { labelKey: "usersGroup", href: "/user-groups" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/user-group/assign/user/:id",
    crumbs: () => [
      general(),
      { labelKey: "usersGroup", href: "/user-groups" },
      { labelKey: "assignUser" },
    ],
  },
  {
    path: "/user-group/:id",
    crumbs: () => [
      general(),
      { labelKey: "usersGroup", href: "/user-groups" },
      { labelKey: "Details" },
    ],
  },

  // Client groups
  {
    path: "/client-groups",
    crumbs: () => [general(), { labelKey: "clientsGroup" }],
  },
  {
    path: "/client-group/create",
    crumbs: () => [
      general(),
      { labelKey: "clientsGroup", href: "/client-groups" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/client-group/assign/client/:id",
    crumbs: () => [
      general(),
      { labelKey: "clientsGroup", href: "/client-groups" },
      { labelKey: "assignClient" },
    ],
  },
  {
    path: "/client-group/:id",
    crumbs: () => [
      general(),
      { labelKey: "clientsGroup", href: "/client-groups" },
      { labelKey: "Details" },
    ],
  },

  // Activity / integration logs
  {
    path: "/activity-logs",
    crumbs: () => [general(), { labelKey: "ActivityLogs" }],
  },
  {
    path: "/integration-logs",
    crumbs: () => [general(), { labelKey: "integrationLogs" }],
  },

  // Manufacturers
  {
    path: "/manufacturers",
    crumbs: () => [general(), { labelKey: "manufacturers" }],
  },
  {
    path: "/manufacturer/create",
    crumbs: () => [
      general(),
      { labelKey: "manufacturers", href: "/manufacturers" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/manufacturer/:id",
    crumbs: () => [
      general(),
      { labelKey: "manufacturers", href: "/manufacturers" },
      { labelKey: "Details" },
    ],
  },
];

export const getGeneralBreadcrumbs = (
  pathname: string,
): BreadcrumbItem[] | null => {
  for (const rule of GENERAL_BREADCRUMB_RULES) {
    const match = matchPath({ path: rule.path, end: true }, pathname);
    if (match) {
      return rule.crumbs(match.params as Record<string, string | undefined>);
    }
  }
  return null;
};
